// OpenRouteService (openrouteservice.org) : itinéraires routiers avec étapes de guidage.
// Service fiable avec une clé gratuite (EXPO_PUBLIC_ORS_KEY dans l'app, VITE_ORS_KEY sur le site).
// Ses réponses sont converties au format des étapes OSRM déjà utilisé par le guidage (maneuvers.ts).
// Logique pure (fetch uniquement), conversion testée dans __tests__/ors.test.ts.

import type { LatLng } from './geo';
import type { OsrmStep } from './maneuvers';

const ORS_URL = 'https://api.openrouteservice.org';
const TIMEOUT_MS = 20000; // une requête sans réponse au-delà est abandonnée

/** Signal qui coupe la requête après `ms`, ou quand `parent` est annulé. */
export function timeoutSignal(ms: number, parent?: AbortSignal): { signal: AbortSignal; done: () => void } {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  const onAbort = () => ctrl.abort();
  parent?.addEventListener('abort', onAbort);
  return {
    signal: ctrl.signal,
    done: () => {
      clearTimeout(timer);
      parent?.removeEventListener('abort', onAbort);
    },
  };
}

/** Clé OpenRouteService (vide = service non configuré : on garde OSRM). */
export const orsKey = (): string => (process.env.EXPO_PUBLIC_ORS_KEY ?? '').trim();

export type OrsRoute = {
  points: LatLng[];
  distance: number;
  duration: number;
  steps: OsrmStep[];
  /** indice, dans `points`, du début de chaque étape */
  stepStarts: number[];
};

type OrsStep = {
  type: number;
  name?: string;
  exit_number?: number;
  way_points: [number, number];
};

/** Type d'étape OpenRouteService -> manœuvre au format OSRM. */
function toOsrmManeuver(step: OrsStep): Omit<OsrmStep['maneuver'], 'location'> {
  switch (step.type) {
    case 0:
      return { type: 'turn', modifier: 'left' };
    case 1:
      return { type: 'turn', modifier: 'right' };
    case 2:
      return { type: 'turn', modifier: 'sharp left' };
    case 3:
      return { type: 'turn', modifier: 'sharp right' };
    case 4:
      return { type: 'turn', modifier: 'slight left' };
    case 5:
      return { type: 'turn', modifier: 'slight right' };
    case 6:
      return { type: 'continue', modifier: 'straight' };
    case 7:
      return { type: 'roundabout', modifier: 'right', exit: step.exit_number };
    case 8:
      return { type: 'exit roundabout' };
    case 9:
      return { type: 'continue', modifier: 'uturn' };
    case 10:
      return { type: 'arrive' };
    case 11:
      return { type: 'depart' };
    case 12:
      return { type: 'fork', modifier: 'left' };
    case 13:
      return { type: 'fork', modifier: 'right' };
    default:
      return { type: 'notification' };
  }
}

/** Réponse GeoJSON de /v2/directions/{profil}/geojson -> points + étapes OSRM. */
export function parseOrsDirections(json: unknown): OrsRoute | null {
  const feature = (json as { features?: any[] })?.features?.[0];
  const coords = feature?.geometry?.coordinates as [number, number][] | undefined;
  if (!feature || !Array.isArray(coords) || coords.length < 2) return null;
  const segments: { steps?: OrsStep[] }[] = feature.properties?.segments ?? [];
  const raw = segments.flatMap((s) => s.steps ?? []);
  const stepStarts = raw.map((s) => Math.min(Math.max(0, s.way_points?.[0] ?? 0), coords.length - 1));
  const steps: OsrmStep[] = raw.map((s) => {
    const at = coords[Math.min(Math.max(0, s.way_points?.[0] ?? 0), coords.length - 1)];
    const name = s.name && s.name !== '-' ? s.name : undefined;
    return { name, maneuver: { ...toOsrmManeuver(s), location: [at[0], at[1]] as [number, number] } };
  });
  const summary = feature.properties?.summary ?? {};
  return {
    points: coords.map(([lon, lat]) => ({ latitude: lat, longitude: lon })),
    distance: summary.distance ?? 0,
    duration: summary.duration ?? 0,
    steps,
    stepStarts,
  };
}

/**
 * Itinéraire en voiture passant par `points` (2 à 50). `radius` : distance max (m) pour
 * accrocher chaque point à une route. null si le service ne trouve pas d'itinéraire.
 */
export async function fetchOrsDirections(
  points: LatLng[],
  opts: { radius?: number; signal?: AbortSignal; bearings?: number[] } = {},
): Promise<OrsRoute | null> {
  const body: Record<string, unknown> = {
    coordinates: points.map((p) => [Number(p.longitude.toFixed(6)), Number(p.latitude.toFixed(6))]),
    instructions: true,
  };
  if (opts.radius) body.radiuses = points.map(() => opts.radius);
  // Attention : le sens imposé (bearings) prive OpenRouteService de son calcul rapide ;
  // avec de nombreux points de passage, la réponse peut alors prendre plus de 30 s.
  if (opts.bearings?.length === points.length) body.bearings = opts.bearings.map((b) => [Math.round(b), 45]);
  const limit = timeoutSignal(TIMEOUT_MS, opts.signal);
  let res: Response;
  try {
    res = await fetch(`${ORS_URL}/v2/directions/driving-car/geojson`, {
      method: 'POST',
      signal: limit.signal,
      headers: {
        Authorization: orsKey(),
        'Content-Type': 'application/json',
        Accept: 'application/geo+json, application/json',
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    limit.done();
    // délai dépassé (et pas une annulation demandée par l'appelant)
    if ((e as Error).name === 'AbortError' && !opts.signal?.aborted) {
      throw new Error('OpenRouteService : pas de réponse (délai dépassé)');
    }
    throw e;
  }
  limit.done();
  if (!res.ok) {
    // message d'OpenRouteService (clé refusée, point loin d'une route…) pour le diagnostic
    const detail = await res
      .json()
      .then((j) => j?.error?.message ?? j?.error ?? j?.message)
      .catch(() => null);
    throw new Error(`OpenRouteService ${res.status}${detail ? ` : ${String(detail)}` : ''}`);
  }
  return parseOrsDirections(await res.json());
}
